import { Sine, TimelineLite, TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import { GalleryQuiltData } from "../Data/GalleryQuiltData";
import { PatchData } from "../Data/PatchData";
import * as SoundEffect from "../Data/SoundEffect";
import * as Topics from "../Data/Topics";
import { IOnboardingTarget } from "../GameObjects/IOnboardingTarget";
import { Patch } from "../GameObjects/Patch";
import { PatchDraggable } from "../GameObjects/PatchDraggable";
import { QuiltFrame } from "../GameObjects/QuiltFrame";
import { QuiltGridFocusable } from "../GameObjects/QuiltGridFocusable";
import * as QuiltUtils from "../Utils/QuiltUtils";

export class Quilt extends HLF.Input.Receptacle implements IOnboardingTarget {
    private _frame: QuiltFrame;
    private _preview!: Patch;
    private _patches: Patch[];
    private _focusables: QuiltGridFocusable[];
    private _quiltIndex: number;

    constructor(pIndex: number) {
        super();
        this._quiltIndex = pIndex;

        this._frame = new QuiltFrame();
        this.addChild(this._frame);

        this._patches = new Array<Patch>();

        this.hitArea = new PIXI.Rectangle(
            -QuiltUtils.GRID_SIZE.x * 0.5 * QuiltUtils.TILE_WIDTH,
            -QuiltUtils.GRID_SIZE.y * 0.5 * QuiltUtils.TILE_WIDTH,
            QuiltUtils.GRID_SIZE.x * QuiltUtils.TILE_WIDTH,
            QuiltUtils.GRID_SIZE.y * QuiltUtils.TILE_WIDTH,
        );

        this._focusables = new Array<QuiltGridFocusable>();
        for (let i = 0; i < QuiltUtils.GRID_SIZE.x; ++i) {
            for (let j = 0; j < QuiltUtils.GRID_SIZE.y; ++j) {
                const focusable: QuiltGridFocusable = new QuiltGridFocusable(
                    this.onFocusableBeginFocus.bind(this),
                    this.onFocusableActivated.bind(this),
                    this.onFocusableEndFocus.bind(this),
                );
                const origin: PIXI.Point = new PIXI.Point(
                    -(QuiltUtils.GRID_SIZE.x - 1) * 0.5 * QuiltUtils.TILE_WIDTH,
                    -(QuiltUtils.GRID_SIZE.y - 1) * 0.5 * QuiltUtils.TILE_WIDTH,
                );
                focusable.position.set(
                    origin.x + QuiltUtils.TILE_WIDTH * i,
                    origin.y + QuiltUtils.TILE_WIDTH * j,
                );
                this.addChild(focusable);
                this._focusables.push(focusable);
            }
        }
    }

    public getOnboardingPosition(): PIXI.Point {
        return this.getGlobalPosition();
    }

    public getOnboardingRotation(): number {
        return 0;
    }

    public addPatch(pPatch: Patch, pGridPos: PIXI.Point, pInstantly: boolean = false) {
        this.addChild(pPatch);
        this._patches.push(pPatch);
        pPatch.position = QuiltUtils.gridToLocal(pGridPos);
        pPatch.showStitches();
        this.checkObscured(pPatch);
        if (pInstantly === false) {
            PubSub.publishSync(HLF.PLAY_AUDIO, new HLF.AudioToken(SoundEffect.STITCH_SOUND, 1, false,
                HLF.AudioCategory.SFX));
            PubSub.publishSync(Topics.PATCH_ADDED, undefined);
        }

    }

    public previewPatch(pGridPos: PIXI.Point): void {
        const pos: PIXI.Point = this.clampGridPos(pGridPos, this._preview);
        this._preview.visible = true;
        this._preview.alpha = 0.85;
        this._preview.position.copy(QuiltUtils.gridToLocal(pos));
    }

    public clone(): Quilt {
        const quilt: Quilt = new Quilt(this._quiltIndex);
        for (let i = 0; i < this._patches.length; ++i) {
            quilt.addPatch(this._patches[i].clone(), QuiltUtils.localToGrid(this._patches[i].position), true);
        }
        return quilt;
    }

    public clear(): TimelineLite {
        this._isActive = false;

        const timeline: TimelineLite = new TimelineLite();
        for (let i = 0; i < this._patches.length; ++i) {
            timeline.to(this._patches[i], 0.35, {
                alpha: 0,
                ease: Sine.easeInOut,
            }, 0);
        }
        timeline.call(() => {
            for (let i = 0; i < this._patches.length; ++i) {
                this.removeChild(this._patches[i]);
            }
            this._patches = [];
            this._isActive = true;
        });
        return timeline;
    }

    public generateSaveData(): GalleryQuiltData {
        const patches: PatchData[] = new Array<PatchData>();
        const positions: PIXI.Point[] = new Array<PIXI.Point>();
        for (let i = 0; i < this._patches.length; ++i) {
            patches.push(this._patches[i].data);
            positions.push(QuiltUtils.localToGrid(this._patches[i].position));
        }
        return new GalleryQuiltData(this._quiltIndex, patches, positions);
    }

    protected onDragBegin(pTopic: string, pDraggable: HLF.Input.Draggable): void {
        if (this._isActive && pDraggable instanceof PatchDraggable) {
            super.onDragBegin(pTopic, pDraggable);
            this._preview = pDraggable.preview;
            this._preview.visible = false;
            this.addChild(this._preview);
        }
    }

    protected onDragEnd(pTopic: string, pDraggable: HLF.Input.Draggable): void {
        if (this._isActive && pDraggable instanceof PatchDraggable) {
            this._dragged!.alpha = 1;
            pDraggable.visible = true;
            super.onDragEnd(pTopic, pDraggable);
            this.removeChild(this._preview);
        }
    }

    protected onPointerDown(pEvent: PIXI.interaction.InteractionEvent): void {
        super.onPointerDown(pEvent);
        this.onPointerMove(pEvent);
    }

    protected onPointerMove(pEvent: PIXI.interaction.InteractionEvent): void {
        super.onPointerMove(pEvent);
        const draggable: HLF.Input.Draggable = this._dragged !== undefined ? this._dragged : this._selected!;
        if (draggable !== undefined) {
            const pos: PIXI.Point = pEvent.data.getLocalPosition(this);
            if (this.hitArea.contains(pos.x, pos.y)) {
                this.previewPatch(QuiltUtils.localToGrid(pos));
                if (this._dragged !== undefined) {
                    // For some reason, setting visible to false makes it permanently invisible after placing. Setting
                    // alpha to zero works
                    this._dragged.alpha = 0;
                }
            }
            else {
                if (this._dragged !== undefined) {
                    this._dragged.alpha = 1;
                }
            }
        }
    }

    protected onPointerOut(): void {
        super.onPointerOut();
        this._preview.visible = false;
    }

    protected onDraggableSelected(pTopic: string, pDraggable: HLF.Input.Draggable): void {
        if (this._isActive && pDraggable instanceof PatchDraggable) {
            super.onDraggableSelected(pTopic, pDraggable);
            this._preview = pDraggable.preview;
            this._preview.visible = false;
            this.addChild(this._preview);
            this.updateFocusableSize(pDraggable.patch);
            PubSub.publishSync(HLF.REGISTER_FOCUSABLES, this.getValidFocusables(pDraggable.patch));
        }
    }

    protected onDraggableDeselected(pTopic: string, pDraggable: HLF.Input.Draggable): void {
        if (this._isActive && pDraggable instanceof PatchDraggable) {
            super.onDraggableDeselected(pTopic, pDraggable);
            this.removeChild(this._preview);
        }
        PubSub.publishSync(HLF.UNREGISTER_FOCUSABLES, this._focusables);
    }

    protected addDraggable(pDraggable: HLF.Input.Draggable): void {
        if (pDraggable instanceof PatchDraggable) {
            super.addDraggable(pDraggable);
            this.addPatch(pDraggable.preview.clone(), QuiltUtils.localToGrid(pDraggable.preview.position), false);
            HLF.PixiUtils.sendToFront(pDraggable.preview);
        }
    }

    private onFocusableBeginFocus(pFocusable: QuiltGridFocusable): void {
        const localPos: PIXI.Point = this.transform.worldTransform.applyInverse(pFocusable.getGlobalPosition());
        this.previewPatch(QuiltUtils.localToGrid(localPos));
    }

    private onFocusableActivated(pFocusable: QuiltGridFocusable): void {
        this.addDraggable(this._selected!);
    }

    private onFocusableEndFocus(pFocusable: QuiltGridFocusable): void {
        this._preview.visible = false;
    }

    private checkObscured(pPatch: Patch): void {
        const gridPos: PIXI.Point = QuiltUtils.localToGrid(pPatch.position);
        for (let y = 0; y < pPatch.tileHeight; ++y) {
            for (let x = 0; x < pPatch.tileWidth; ++x) {
                if (pPatch.tiles[y][x] !== undefined) {
                    for (let i = this._patches.length - 2; i >= 0; --i) {
                        this.checkObscuredAtTile(this._patches[i], gridPos.x + x, gridPos.y + y);
                    }
                }
            }
        }
        // delete any completely obscured patches
        for (let i = this._patches.length - 1; i >= 0; --i) {
            if (this._patches[i].obscured) {
                const patch: Patch = this._patches[i];
                this._patches.splice(i, 1);
                // Delay the removal to allow for stitch fade out animation to finish
                new TimelineLite().add(() => {
                    this.removeChild(patch);
                }, 0.5);
            }
        }
    }

    private checkObscuredAtTile(pPatch: Patch, pX: number, pY: number): void {
        const gridPos: PIXI.Point = QuiltUtils.localToGrid(pPatch.position);
        for (let y = 0; y < pPatch.tileHeight; ++y) {
            for (let x = 0; x < pPatch.tileWidth; ++x) {
                if (pPatch.tiles[y][x] === undefined) {
                    continue;
                }
                const deltaX: number = pX - (gridPos.x + x);
                const deltaY: number = pY - (gridPos.y + y);
                if (deltaX === 0 && deltaY === 0) {
                    pPatch.tiles[y][x].obscured = true;
                }
                else if (deltaX === 0 && deltaY === -1) {
                    pPatch.tiles[y][x].hideStitches(QuiltUtils.Direction.NORTH);
                }
                else if (deltaX === 0 && deltaY === 1) {
                    pPatch.tiles[y][x].hideStitches(QuiltUtils.Direction.SOUTH);
                }
                else if (deltaX === 1 && deltaY === 0) {
                    pPatch.tiles[y][x].hideStitches(QuiltUtils.Direction.EAST);
                }
                else if (deltaX === -1 && deltaY === 0) {
                    pPatch.tiles[y][x].hideStitches(QuiltUtils.Direction.WEST);
                }
            }
        }
    }

    private clampGridPos(pPos: PIXI.Point, pPatch: Patch): PIXI.Point {
        const clamped: PIXI.Point = pPos.clone();
        clamped.x = Math.min(pPos.x, QuiltUtils.GRID_SIZE.x - 1 - (pPatch.tiles[0].length - 1) );
        clamped.y = Math.min(pPos.y, QuiltUtils.GRID_SIZE.y - 1 - (pPatch.tiles.length - 1));
        return clamped;
    }

    private getValidFocusables(pPatch: Patch): QuiltGridFocusable[] {
        const valid: QuiltGridFocusable[] = new Array<QuiltGridFocusable>();

        for (let i = 0; i < QuiltUtils.GRID_SIZE.x; ++i) {
            for (let j = 0; j < QuiltUtils.GRID_SIZE.y; ++j) {
                if (
                    i <= (QuiltUtils.GRID_SIZE.x - pPatch.tileWidth) &&
                    j <= (QuiltUtils.GRID_SIZE.y - pPatch.tileHeight)
                ) {
                    valid.push(this._focusables[j + i * QuiltUtils.GRID_SIZE.x]);
                }
            }
        }
        return valid;
    }

    private updateFocusableSize(pPatch: Patch): void {
        for (let i = 0; i < this._focusables.length; ++i) {
            this._focusables[i].setSize(pPatch.tileWidth, pPatch.tileHeight);
        }
    }
}
